"use client";

import type { SliderApi, SliderOptions } from "@components/composers/slider/Slider";
import type { ILink } from "@components/core/Link";
import type React from "react";

import { Slider, SliderContent, SliderItem } from "@components/composers/slider/Slider";
import { SliderControls, SliderNext, SliderPrevious } from "@components/composers/slider/SliderControls";
import { Link } from "@components/core/Link";
import { isValidLinkWithLabel } from "@ui/lib/link";
import { clamp, range } from "@ui/lib/math";
import { cn } from "@ui/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

export interface SliderExpressiveProps {
  className?: string;
  // Reduce margin between items on large screens
  compact?: boolean;
  cta?: ILink;
  inverse?: boolean;
  items: React.ReactNode[];
}

const TWEEN_FACTOR_BASE = 0.46;

const SliderExpressive = ({ items, inverse, className, compact = false, cta }: SliderExpressiveProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [api, setApi] = useState<SliderApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const options = {
    align: "center",
    loop: true,
    slides: ".slider-item",
  } satisfies SliderOptions;

  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLElement[]>([]);

  const setTweenNodes = useCallback((emblaApi: SliderApi): void => {
    tweenNodes.current =
      emblaApi?.slideNodes().map(slideNode => {
        return slideNode.querySelector(".slider-item-content") as HTMLElement;
      }) || [];
  }, []);

  const setTweenFactor = useCallback((emblaApi: SliderApi) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * (emblaApi?.scrollSnapList().length || 0);
  }, []);

  const tweenScale = useCallback((emblaApi: SliderApi, eventName?: string) => {
    if (!emblaApi) return;
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();
    const slidesInView = emblaApi.slidesInView();
    const isScrollEvent = eventName === "scroll";
    const activeIndex = emblaApi.selectedScrollSnap();
    setActiveIndex(activeIndex);

    emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress;
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach(slideIndex => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach(loopItem => {
            const target = loopItem.target();

            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);

              if (sign === -1) {
                diffToTarget = scrollSnap - (1 + scrollProgress);
              }
              if (sign === 1) {
                diffToTarget = scrollSnap + (1 - scrollProgress);
              }
            }
          });
        }

        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
        const rangeScaled = range(0, 1, 0.35, 1, tweenValue);
        const scale = clamp(rangeScaled, 0.35, 1).toString();
        const tweenNode = tweenNodes.current[slideIndex];
        const parent = tweenNode.parentElement;
        tweenNode.style.transform = `scale(${scale})`;
        if (parent) {
          parent.style.pointerEvents = "";
        }
        if (tweenValue > 0.5) {
          tweenNode.style.opacity = range(0.5, 1, 0.2, 1, tweenValue).toString();
        } else if (tweenValue > 0.25) {
          tweenNode.style.opacity = range(0.25, 0.4999, 0, 0.2, tweenValue).toString();
        } else {
          tweenNode.style.opacity = "0";
          if (parent) {
            parent.style.pointerEvents = "none";
          }
        }
        if (diffToTarget === 0) {
          tweenNode.style.zIndex = "10";
        } else {
          tweenNode.style.zIndex = "";
        }
      });
    });
  }, []);

  useEffect(() => {
    if (!api) return;

    if (sliderRef.current) {
      sliderRef.current.classList.remove("opacity-0");
    }

    setTweenNodes(api);
    setTweenFactor(api);
    tweenScale(api);

    api
      .on("reInit", setTweenNodes)
      .on("reInit", setTweenFactor)
      .on("reInit", tweenScale)
      .on("scroll", tweenScale)
      .on("select", tweenScale);

    return () => {
      api
        .off("reInit", setTweenNodes)
        .off("reInit", setTweenFactor)
        .off("reInit", tweenScale)
        .off("scroll", tweenScale)
        .off("select", tweenScale);
    };
  }, [api, setTweenFactor, setTweenNodes, tweenScale, sliderRef]);

  const hasCta = isValidLinkWithLabel(cta);
  const _items = items;

  // Add extra slides to make the slider loop correctly
  while (items.length < 6) {
    _items.push(...items);
  }

  const goToItem = useCallback(
    (index: number) => {
      if (api && index !== activeIndex) {
        api.scrollTo(index);
      }
    },
    [activeIndex, api],
  );

  return (
    <Slider
      className={cn(
        "slider-expressive relative overflow-x-hidden opacity-0 transition-opacity duration-700 ease-out-slow",
        className,
      )}
      opts={options}
      ref={sliderRef}
      setApi={setApi}
    >
      <SliderContent className={cn("breakout py-4", compact ? "lg:breakout-reset" : "")}>
        {_items?.map((item, index) => (
          <SliderItem
            className={cn([
              "slider-item relative z-1",
              "-mr-[10%] pl-0",
              compact ? "lg:-mr-3-cols" : "lg:mr-2-cols-no-gutter xxl:w-5-cols-vw",
              "w-10-cols-vw lg:w-4-cols-vw",
              "effect-focus-before transform-gpu",
              index === activeIndex ? "slider-item-active z-10 px-0 opacity-100" : "cursor-pointer",
            ])}
            key={index}
            onClick={() => {
              goToItem(index);
            }}
          >
            <div className={cn("slider-item-content", index === activeIndex ? "" : "pointer-events-none select-none")}>
              {item}
            </div>
          </SliderItem>
        ))}
      </SliderContent>
      {!compact && (
        <div className='w-4-cols-vw pointer-events-none absolute bottom-0 left-1/2 top-0 hidden -translate-x-1/2 lg:flex'>
          <div className='-ml-inner-gutter -left-1-cols pointer-events-auto absolute top-1/2 -translate-y-1/2'>
            <SliderPrevious size='pill-sm' variant={inverse ? "ghost-inverse" : "ghost"} />
          </div>
          <div className='-mr-inner-gutter -right-1-cols pointer-events-auto absolute top-1/2 -translate-y-1/2'>
            <SliderNext size='pill-sm' variant={inverse ? "ghost-inverse" : "ghost"} />
          </div>
        </div>
      )}
      <div
        className={cn(
          "container mt-40 flex items-center",
          hasCta ? "justify-between lg:justify-center" : "justify-center",
          !hasCta && !compact ? "lg:hidden" : "",
        )}
      >
        <SliderControls className={cn(compact ? "" : "lg:hidden")} inverse={inverse} />
        {hasCta && <Link asButton buttonVariants={{ variant: inverse ? "outline-inverse" : "elevated" }} {...cta!} />}
      </div>
    </Slider>
  );
};
SliderExpressive.displayName = "SliderExpressive";

export { SliderExpressive };
